/**
 * Dial codes for admin forms (create app user, edit profile, admin panel users).
 * Sorted longest-first for parsing numbers that start with a code.
 *
 * nationalMin / nationalMax = allowed length of subscriber digits (after country code), inclusive.
 * Omit for a country to use defaults (min 6, max 15) via getNationalLengthLimits.
 */
export const PHONE_DIAL_CODES = [
  { code: "+358", country: "Finland", nationalMin: 6, nationalMax: 12 },
  { code: "+880", country: "Bangladesh", nationalMin: 10, nationalMax: 10 },
  { code: "+971", country: "UAE", nationalMin: 9, nationalMax: 9 },
  { code: "+966", country: "Saudi Arabia", nationalMin: 9, nationalMax: 9 },
  { code: "+977", country: "Nepal", nationalMin: 10, nationalMax: 10 },
  { code: "+234", country: "Nigeria", nationalMin: 10, nationalMax: 10 },
  { code: "+254", country: "Kenya", nationalMin: 9, nationalMax: 9 },
  { code: "+92", country: "Pakistan", nationalMin: 10, nationalMax: 10 },
  { code: "+91", country: "India", nationalMin: 10, nationalMax: 10 },
  { code: "+86", country: "China", nationalMin: 11, nationalMax: 11 },
  { code: "+81", country: "Japan", nationalMin: 10, nationalMax: 10 },
  { code: "+82", country: "South Korea", nationalMin: 9, nationalMax: 11 },
  { code: "+84", country: "Vietnam", nationalMin: 9, nationalMax: 9 },
  { code: "+90", country: "Turkey", nationalMin: 10, nationalMax: 10 },
  { code: "+94", country: "Sri Lanka", nationalMin: 9, nationalMax: 9 },
  { code: "+65", country: "Singapore", nationalMin: 8, nationalMax: 8 },
  { code: "+60", country: "Malaysia", nationalMin: 9, nationalMax: 10 },
  { code: "+62", country: "Indonesia", nationalMin: 9, nationalMax: 11 },
  { code: "+66", country: "Thailand", nationalMin: 9, nationalMax: 9 },
  { code: "+61", country: "Australia", nationalMin: 9, nationalMax: 9 },
  { code: "+64", country: "New Zealand", nationalMin: 8, nationalMax: 10 },
  { code: "+55", country: "Brazil", nationalMin: 10, nationalMax: 11 },
  { code: "+52", country: "Mexico", nationalMin: 10, nationalMax: 10 },
  { code: "+54", country: "Argentina", nationalMin: 10, nationalMax: 11 },
  { code: "+27", country: "South Africa", nationalMin: 9, nationalMax: 9 },
  { code: "+20", country: "Egypt", nationalMin: 10, nationalMax: 10 },
  { code: "+49", country: "Germany", nationalMin: 10, nationalMax: 11 },
  { code: "+33", country: "France", nationalMin: 9, nationalMax: 9 },
  { code: "+39", country: "Italy", nationalMin: 9, nationalMax: 10 },
  { code: "+34", country: "Spain", nationalMin: 9, nationalMax: 9 },
  { code: "+31", country: "Netherlands", nationalMin: 9, nationalMax: 9 },
  { code: "+32", country: "Belgium", nationalMin: 9, nationalMax: 9 },
  { code: "+41", country: "Switzerland", nationalMin: 9, nationalMax: 9 },
  { code: "+46", country: "Sweden", nationalMin: 9, nationalMax: 9 },
  { code: "+47", country: "Norway", nationalMin: 8, nationalMax: 8 },
  { code: "+45", country: "Denmark", nationalMin: 8, nationalMax: 8 },
  { code: "+48", country: "Poland", nationalMin: 9, nationalMax: 9 },
  { code: "+351", country: "Portugal", nationalMin: 9, nationalMax: 9 },
  { code: "+44", country: "UK", nationalMin: 10, nationalMax: 10 },
  { code: "+1", country: "USA/Canada", nationalMin: 10, nationalMax: 10 },
  { code: "+7", country: "Russia", nationalMin: 10, nationalMax: 10 },
].sort((a, b) => b.code.length - a.code.length);

const DEFAULT_DIAL = "+92";

const DEFAULT_NATIONAL_LIMITS = { min: 6, max: 15 };

/**
 * Min/max length of national digits (after dial code) for the selected country.
 */
export function getNationalLengthLimits(dialCode) {
  const d = String(dialCode || "").trim();
  const entry = PHONE_DIAL_CODES.find((pc) => pc.code === d);
  if (!entry || (entry.nationalMin == null && entry.nationalMax == null)) {
    return { ...DEFAULT_NATIONAL_LIMITS };
  }
  return {
    min: entry.nationalMin ?? DEFAULT_NATIONAL_LIMITS.min,
    max: entry.nationalMax ?? DEFAULT_NATIONAL_LIMITS.max,
  };
}

/** Keep only digits and cap length for the current dial code. */
export function clampNationalDigitsForDial(dialCode, rawInput) {
  const { max } = getNationalLengthLimits(dialCode);
  return String(rawInput || "")
    .replace(/\D/g, "")
    .slice(0, max);
}

/**
 * Split stored contact (e.g. +923001234567) into dial code + national digits.
 */
export function parseContactForDialSelect(contactNo) {
  const raw = String(contactNo || "").trim();
  if (!raw) return { dial: DEFAULT_DIAL, national: "" };
  for (const pc of PHONE_DIAL_CODES) {
    if (raw.startsWith(pc.code)) {
      return {
        dial: pc.code,
        national: raw.slice(pc.code.length).replace(/\D/g, ""),
      };
    }
  }
  if (raw.startsWith("+")) {
    const digits = raw.slice(1).replace(/\D/g, "");
    return { dial: DEFAULT_DIAL, national: digits };
  }
  return { dial: DEFAULT_DIAL, national: raw.replace(/\D/g, "") };
}

/** Combine dial + digits only (no spaces). Backend normalizes further. */
export function buildFullContactNo(dial, nationalDigits) {
  const d = String(nationalDigits || "").replace(/\D/g, "");
  const prefix = String(dial || DEFAULT_DIAL).trim();
  return `${prefix}${d}`;
}

/** Latest calendar date (YYYY-MM-DD) for someone who is exactly 18 today. */
export function maxBirthDateIsoForAge18() {
  const t = new Date();
  t.setFullYear(t.getFullYear() - 18);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function minBirthDateIso120Years() {
  const t = new Date();
  t.setFullYear(t.getFullYear() - 120);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
